import { css, Global } from '@emotion/react'
import { Divider } from '@mui/joy'
import dayjs from 'dayjs'
import { pick, round } from 'lodash'
import React from 'react'
import { useSearchParams } from 'react-router'
import AnalysisCard, { AnalysisType } from '../components/AnalysisCard'
import CategoryScore, { CategoryScoreType } from '../components/CategoryScore'
import ConversationAnalysis from '../components/ConversationAnalysis'
import DescriptiveFeedback from '../components/DescriptiveFeedback'
import EnglishLevelBar from '../components/EnglishLevelBar'
import EnglishLevelDisplay from '../components/EnglishLevelDisplay'
import FluencyCard from '../components/FluencyCard'
import FluencyResult from '../components/FluencyResult'
import SpeakingMetrics from '../components/SpeakingMetrics'
import WordUsageChart from '../components/WordUsageChart'
import Logo from '@/assets/logo-full-white.png'
import { Column, Flex, Row, Text } from '@/components/core'
import { useLanguage } from '@/contexts/LanguageContext.tsx'
import { classNameBuilder } from '@/helpers'
import {
  FluencyTestResult,
  getFluencyTestResult
} from '@/services/getFluencyTestResult.ts'
import translations from '@/translations'

export default function ExportResult() {
  const [searchParams] = useSearchParams()
  const { setLanguage, language } = useLanguage()

  const [fluencyTestData, setFluencyTestData] =
    React.useState<FluencyTestResult>()

  const code = searchParams.get('code')

  React.useEffect(() => {
    if (code) {
      getFluencyTestResult(code).then((testData) => {
        setFluencyTestData(testData)
      })
    }
  }, [code])

  React.useEffect(() => {
    if (fluencyTestData?.locale) {
      setTimeout(() => {
        setLanguage(fluencyTestData.locale!)
      }, 0)
    }
  }, [fluencyTestData?.locale])

  const cefrLevelsDetails = React.useMemo(() => {
    return [
      {
        level: 'A1',
        titleKey: 'cefr_a1_title',
        descriptionKey: 'cefr_a1_description'
      },
      {
        level: 'A2',
        titleKey: 'cefr_a2_title',
        descriptionKey: 'cefr_a2_description'
      },
      {
        level: 'B1',
        titleKey: 'cefr_b1_title',
        descriptionKey: 'cefr_b1_description'
      },
      {
        level: 'B2',
        titleKey: 'cefr_b2_title',
        descriptionKey: 'cefr_b2_description'
      },
      {
        level: 'C1',
        titleKey: 'cefr_c1_title',
        descriptionKey: 'cefr_c1_description'
      },
      {
        level: 'C2',
        titleKey: 'cefr_c2_title',
        descriptionKey: 'cefr_c2_description'
      }
    ]
  }, [])

  const PageHeader = React.useMemo(() => {
    if (!fluencyTestData) {
      return <div />
    }
    if (fluencyTestData.student.company?.whiteLogoUrl) {
      return (
        <Row className="absolute left-0 top-0 w-full items-center justify-between bg-electric-indigo-900 px-8 py-4">
          <img
            src={`${import.meta.env.VITE_RECORD_BASE_URL}/${
              fluencyTestData.student.company.whiteLogoUrl
            }`}
            alt="logo-company"
            style={{ width: 'auto', objectFit: 'contain', height: 32 }}
          />
          <img
            src={Logo}
            alt="logo"
            style={{ width: 'auto', objectFit: 'contain', height: 18 }}
          />
        </Row>
      )
    }
    return (
      <Row className="absolute left-0 top-0 w-full justify-center bg-electric-indigo-900 px-8 py-4">
        <img
          src={Logo}
          alt="logo"
          style={{ width: 'auto', objectFit: 'contain', height: 32 }}
        />
      </Row>
    )
  }, [fluencyTestData])

  const PageFooter = React.useCallback(
    (page: number) => {
      return (
        <Row className="absolute bottom-0 left-0 w-full justify-between bg-electric-indigo-900 px-8 py-3">
          <Text className="!text-white/[.80]" size="12">
            <Text.Translated
              className="!text-white/[.80]"
              size="12"
              fontWeight="semibold"
              translationKey="complete_report_app_resultado_export_page"
            />{' '}
            <a href={`https://test.fluencyflow.ai/resultado?code=${code}`}>
              https://test.fluencyflow.ai/resultado?code={code}
            </a>
          </Text>
          <Text className="!text-white/[.90]" size="10">
            {translations.formatString(
              translations.page_number_app_resultado_export_page,
              page
            )}
          </Text>
        </Row>
      )
    },
    [code]
  )

  if (!fluencyTestData) return null

  return (
    <>
      <Global
        styles={css`
          @page {
            size: A4;
            margin: 0;
            padding: 0;
          }
          @media print {
            margin: 0;
            padding: 0;
          }

          html,
          body {
            display: block;
            background: white linear-gradient(128deg, #fff, #f9f5ff);
            margin: 0;
            padding: 0;
            width: 211mm;
            height: 298mm;
            line-height: inherit;
          }

          #result,
          #fluency-analysis,
          #grammar-analysis,
          #conversation-analysis {
            break-after: page;
          }
        `}
      />
      <Column>
        <Column
          key={language}
          id="result"
          className="relative h-screen p-8 pb-[50px] pt-[82px]"
        >
          {PageHeader}
          <Column className="gap-1">
            <Text.Translated
              color="500"
              size="xs"
              translationKey="performed_by_app_resultado_export_page"
              values={[fluencyTestData.student.name]}
            />
            <Text.Translated
              color="500"
              size="xs"
              translationKey="performed_on_app_resultado_export_page"
              values={[dayjs(fluencyTestData.finishedAt).format('DD/MM/YYYY')]}
            />
          </Column>
          <Row className="w-full items-center justify-between gap-1">
            <Column className="gap-[8px]">
              <Text.Translated
                size="3xl"
                className="!text-electric-indigo-700"
                fontWeight="bold"
                translationKey="overall_result_app_resultado_export_page"
              />
              <EnglishLevelBar
                isResponsive={false}
                level={fluencyTestData.fluencyDataOverview?.cefrScore?.overall}
              />
            </Column>
            <EnglishLevelDisplay
              isResponsive={false}
              cefrLevel={
                fluencyTestData.fluencyDataOverview?.cefrScore?.overall
              }
            />
          </Row>
          <Column className="gap-[48px] pt-[36px]">
            <Row>
              <DescriptiveFeedback
                size="md"
                feedbacks={fluencyTestData.chatGptOverview?.reasons || []}
              />
            </Row>
            <Row>
              <CategoryScore
                categoryScore={
                  pick(fluencyTestData.fluencyDataOverview.score, [
                    'pronunciation',
                    'fluency',
                    'grammar',
                    'coherence',
                    'vocabulary'
                  ]) as CategoryScoreType
                }
              />
            </Row>
          </Column>
          {PageFooter(1)}
        </Column>
        <Column
          id="fluency-analysis"
          className="relative h-screen p-8 pb-[50px] pt-[82px]"
        >
          {PageHeader}
          <Text.Translated
            size="2xl"
            color="600"
            fontWeight="bold"
            translationKey="fluency_analysis_app_resultado_export_page"
          />
          <FluencyResult
            isResponsive={false}
            fluencyScore={
              (
                fluencyTestData.fluencyDataOverview.score ||
                fluencyTestData.fluencyDataOverview.speechaceScore
              ).fluency
            }
          />
          <div className="h-4" />
          <FluencyCard
            isResponsive={false}
            data={{
              speechRate:
                fluencyTestData.fluencyDataOverview?.fluencyMetrics?.speechRate,
              articulationRate:
                fluencyTestData.fluencyDataOverview?.fluencyMetrics
                  ?.articulationRate
            }}
          />
          <div className="h-4" />
          <Flex className="flex-row flex-wrap justify-between gap-y-[24px]">
            <Column className="w-[49%]">
              <SpeakingMetrics
                isResponsive={false}
                title="speaking_time_speaking_metrics"
                label="seconds_label_speaking_metrics"
                goodValueType="more"
                goodValue={round(
                  fluencyTestData.fluencyDataOverview?.fluencyMetrics
                    ?.duration * 0.8,
                  0
                )}
                currentValue={round(
                  fluencyTestData.fluencyDataOverview?.fluencyMetrics
                    ?.duration -
                    fluencyTestData.fluencyDataOverview?.fluencyMetrics
                      ?.allPauseDuration,
                  1
                )}
                markers={[
                  {
                    limit:
                      fluencyTestData.fluencyDataOverview?.fluencyMetrics
                        ?.duration * 0.7,
                    variant: 'bad'
                  },
                  {
                    limit:
                      fluencyTestData.fluencyDataOverview?.fluencyMetrics
                        ?.duration * 0.8,
                    variant: 'warning'
                  },
                  {
                    limit:
                      fluencyTestData.fluencyDataOverview?.fluencyMetrics
                        ?.duration,
                    variant: 'good'
                  }
                ]}
              />
            </Column>
            <Column className="w-[49%]">
              <SpeakingMetrics
                isResponsive={false}
                title="pause_count_speaking_metrics"
                label="pauses_label_speaking_metrics"
                goodValueType="until"
                goodValue={round(
                  fluencyTestData.fluencyDataOverview?.fluencyMetrics
                    ?.duration - 10,
                  0
                )}
                currentValue={
                  fluencyTestData.fluencyDataOverview?.fluencyMetrics
                    ?.allPauseCount
                }
                markers={[
                  {
                    limit:
                      fluencyTestData.fluencyDataOverview?.fluencyMetrics
                        ?.duration - 10,
                    variant: 'good'
                  },
                  {
                    limit:
                      fluencyTestData.fluencyDataOverview?.fluencyMetrics
                        ?.duration + 15,
                    variant: 'warning'
                  },
                  {
                    limit:
                      fluencyTestData.fluencyDataOverview?.fluencyMetrics
                        ?.duration + 30,
                    variant: 'bad'
                  }
                ]}
              />
            </Column>
            <Column className="w-[49%]">
              <SpeakingMetrics
                isResponsive={false}
                title="total_pause_duration_speaking_metrics"
                label="seconds_label_speaking_metrics"
                goodValueType="until"
                goodValue={round(
                  fluencyTestData.fluencyDataOverview?.fluencyMetrics
                    ?.duration * 0.25,
                  0
                )}
                currentValue={round(
                  fluencyTestData.fluencyDataOverview?.fluencyMetrics
                    ?.allPauseDuration,
                  1
                )}
                markers={[
                  {
                    limit:
                      fluencyTestData.fluencyDataOverview?.fluencyMetrics
                        ?.duration * 0.25,
                    variant: 'good'
                  },
                  {
                    limit:
                      fluencyTestData.fluencyDataOverview?.fluencyMetrics
                        ?.duration * 0.5,
                    variant: 'warning'
                  },
                  {
                    limit:
                      fluencyTestData.fluencyDataOverview?.fluencyMetrics
                        ?.duration,
                    variant: 'bad'
                  }
                ]}
              />
            </Column>
            <Column className="w-[49%]">
              <SpeakingMetrics
                isResponsive={false}
                title="longest_phrase_without_hesitation_speaking_metrics"
                label="seconds_label_speaking_metrics"
                goodValue={4}
                goodValueType="more"
                currentValue={round(
                  fluencyTestData.fluencyDataOverview?.fluencyMetrics
                    ?.maxLengthRun,
                  1
                )}
                markers={[
                  {
                    limit: 2,
                    variant: 'bad'
                  },
                  {
                    limit: 4,
                    variant: 'warning'
                  },
                  {
                    limit: 6,
                    variant: 'good'
                  }
                ]}
              />
            </Column>
          </Flex>
          {PageFooter(2)}
        </Column>
        <Column
          id="grammar-analysis"
          className="relative h-screen p-8 pb-[50px] pt-[82px]"
        >
          {PageHeader}
          <Text.Translated
            size="2xl"
            color="600"
            fontWeight="bold"
            translationKey="grammar_vocabulary_analysis_app_resultado_export_page"
          />
          <div className="h-6 shrink-0" />
          <Column className="space-y-1">
            <AnalysisCard
              isResponsive={false}
              score={
                (
                  fluencyTestData.fluencyDataOverview.score ||
                  fluencyTestData.fluencyDataOverview.speechaceScore
                ).grammar
              }
              analysisType={AnalysisType.GRAMMAR}
              chartItems={fluencyTestData.fluencyDataOverview?.grammarMetrics}
            />
            <div className="h-1" />
            <AnalysisCard
              isResponsive={false}
              score={
                (
                  fluencyTestData.fluencyDataOverview.score ||
                  fluencyTestData.fluencyDataOverview.speechaceScore
                ).vocabulary
              }
              analysisType={AnalysisType.VOCABULARY}
              chartItems={
                fluencyTestData.fluencyDataOverview?.vocabularyMetrics
              }
            />
            <div className="h-1" />
            <AnalysisCard
              isResponsive={false}
              score={
                (
                  fluencyTestData.fluencyDataOverview.score ||
                  fluencyTestData.fluencyDataOverview.speechaceScore
                ).coherence
              }
              analysisType={AnalysisType.COHERENCE}
              chartItems={fluencyTestData.fluencyDataOverview?.coherenceMetrics}
            />
          </Column>
          {PageFooter(3)}
        </Column>
        <Column
          id="conversation-analysis"
          className="relative h-screen p-8 pb-[50px] pt-[82px]"
        >
          {PageHeader}
          <Text.Translated
            size="2xl"
            color="600"
            fontWeight="bold"
            translationKey="conversation_analysis_app_resultado_export_page"
          />
          <ConversationAnalysis
            items={[
              {
                id: 'grammarErrors',
                label: 'grammar_errors_conversation_analysis',
                value: fluencyTestData.chat?.reduce(
                  (acc, chat) => acc + (chat.grammarErrors?.length || 0),
                  0
                )
              },
              ...Object.entries(
                pick(
                  fluencyTestData.vocabularyDataOverview,
                  'sentenceCount',
                  'wordCount',
                  'uniqueWordCount',
                  'averageWordCountBySentence',
                  'averageUniqueWordCountBySentence',
                  'syllableCount',
                  'wordCountWithTwoOrMoreSyllables'
                )
              ).map(([key, value]) => {
                return {
                  id: key,
                  label:
                    {
                      sentenceCount: 'sentences_conversation_analysis',
                      wordCount: 'words_conversation_analysis',
                      averageWordCountBySentence:
                        'average_words_per_sentence_conversation_analysis',
                      uniqueWordCount: 'unique_words_conversation_analysis',
                      averageUniqueWordCountBySentence:
                        'average_unique_words_per_sentence_conversation_analysis',
                      syllableCount: 'total_syllables_conversation_analysis',
                      wordCountWithTwoOrMoreSyllables:
                        'words_with_two_or_more_syllables_conversation_analysis'
                    }[key] || '',
                  value
                }
              })
            ]}
          />
          <div className="h-8" />
          {fluencyTestData.vocabularyDataOverview && (
            <Flex
              className={classNameBuilder(
                ' w-full flex-col justify-between rounded-2xl border border-gray-50 bg-white p-8'
              )}
            >
              <Text.Translated
                size="md"
                fontWeight="bold"
                className="text-[#353535]"
                translationKey="advanced_words_usage_app_resultado_export_page"
              />
              <WordUsageChart
                isResponsive={false}
                data={fluencyTestData.vocabularyDataOverview.wordsByCefrLevel}
              />
            </Flex>
          )}
          {PageFooter(4)}
        </Column>
        <Column
          id="cefr-details"
          className="relative h-screen space-y-4 p-8 pb-[50px] pt-[82px]"
        >
          {PageHeader}
          <Text.Translated
            size="2xl"
            color="600"
            fontWeight="bold"
            translationKey="cefr_reference_app_resultado_export_page"
          />
          <div className="h-2" />
          {cefrLevelsDetails.map((item, index) => (
            <>
              {!!index && (
                <div className="px-6">
                  <Divider />
                </div>
              )}
              <Row className="items-center gap-6" key={item.level}>
                <Column
                  className={classNameBuilder(
                    'h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-full ',
                    [
                      'bg-electric-indigo-300',
                      'bg-electric-indigo-400',
                      'bg-electric-indigo-500',
                      'bg-electric-indigo-600',
                      'bg-electric-indigo-700',
                      'bg-electric-indigo-800'
                    ][index]
                  )}
                >
                  <Text
                    fontWeight="bold"
                    transform="uppercase"
                    className={classNameBuilder('text-[42px] text-white')}
                  >
                    {item.level}
                  </Text>
                </Column>
                <Text.Translated
                  size="xs"
                  color="600"
                  translationKey={item.descriptionKey}
                />
              </Row>
            </>
          ))}
          {PageFooter(5)}
        </Column>
      </Column>
    </>
  )
}
